<template>
    <pendo-drawer
        :visible="visible"
        :class-name="`import-feedback-drawer ${currentStepIndex === 0 ? 'import-feedback-drawer--hide-footer' : ''}`"
        size="fullscreen"
        :z-index="2000"
        @open="onOpen"
        @close="confirmExit">
        <template #header>
            <div
                v-if="currentStepIndex === 0"
                data-cy="import-feedback-drawer-title"
                class="import-feedback-drawer__title">
                Connect sources
            </div>
            <div
                v-if="showGuidePollTitle"
                data-cy="import-feedback-drawer-title"
                class="import-feedback-drawer__title">
                Connect guide poll
            </div>
            <div
                v-if="chosenSource === 'NPS' && currentStepIndex !== 0"
                data-cy="import-feedback-drawer-title"
                class="import-feedback-drawer__title">
                Connect NPS
            </div>
            <div
                v-if="chosenSource === 'file' && currentStepIndex !== 0"
                data-cy="import-feedback-drawer-title"
                class="import-feedback-drawer__title">
                Import a CSV file
            </div>
            <div
                v-if="chosenSource === 'unknown' && currentStepIndex !== 0"
                class="import-feedback-drawer__title">
                Import feedback
            </div>
            <div
                v-if="currentStepIndex !== 0"
                class="import-feedback-drawer__breadcrumbs">
                <span
                    v-for="(step, index) in steps"
                    :key="index">
                    <span
                        class="import-feedback-drawer__breadcrumbs-name"
                        :class="{
                            'import-feedback-drawer__breadcrumbs-name--active': isBreadcrumbActive(index),
                        }">
                        {{ step }}
                    </span>
                    <span
                        v-if="index < steps.length - 1"
                        class="import-feedback-drawer__breadcrumbs-separator fa fa-angle-right" />
                </span>
            </div>
            <div class="import-feedback-drawer__exit">
                <pendo-button
                    theme="app"
                    type="secondary"
                    label="Exit"
                    data-cy="import-feedback-drawer-exit"
                    @click="exitImport" />
            </div>
            <pendo-modal
                class="import-feedback-drawer__exit-modal"
                theme="app"
                :visible="showExitWarning"
                title="Exit import?"
                height="auto"
                data-cy="import-feedback-drawer-exit-confirmation"
                :show-close="false"
                :confirm-button-config="{
                    type: 'danger',
                    theme: 'app',
                    label: 'Exit',
                }"
                :cancel-button-config="{
                    type: 'secondary',
                    theme: 'app',
                    label: 'Go back',
                }"
                @cancel="showExitWarning = false"
                @confirm="confirmExit">
                <template #body>
                    Exiting now will have the following consequences:
                    <ul>
                        <li>
                            Import started - The import process will continue in the background.
                        </li>
                        <li>
                            Import not started - Your selections and configuration will be lost.
                        </li>
                    </ul>
                    Are you sure you want to exit?
                </template>
            </pendo-modal>
        </template>
        <template
            #body
            v-if="!fetchingSourceType">
            <import-feedback-drawer-source-chooser
                v-if="isSourceStep"
                @close-drawer="confirmExit"
                @source-selected="chooseSource" />
            <template v-if="chosenSource === 'file'">
                <import-feedback-drawer-file-step
                    v-if="isFileStep"
                    :csv-filename="csvFilename"
                    :dragging="isDragging"
                    @file-uploaded="fileUploaded"
                    @drag-leave="onDragLeave" />
                <import-feedback-drawer-mapping-step
                    v-else-if="isMappingStep"
                    :headers="headers"
                    :preview-data="previewData"
                    :mapping-table-data="mappingTableData"
                    :mapped-columns="mappedColumns"
                    @map-column="onMapColumn" />
                <import-feedback-drawer-fix-issues-step
                    v-else-if="isFixIssuesStep && hasCSVIssues"
                    :filtered-issues="filteredIssues"
                    :missing-accounts-count="missingAccountsCount"
                    :missing-visitors-count="missingVisitorsCount"
                    :mismatch-visitors-and-accounts-count="mismatchVisitorsAndAccountsCount"
                    :missing-visitor-and-account-count="missingVisitorAndAccountCount"
                    :create-missing-visitors-and-accounts="createMissingVisitorsAndAccounts"
                    @update-issue="onFixIssueUpdate"
                    @create-missing-visitors-and-accounts-change="onCreateMissingVisitorsAndAccountsChange" />
                <import-feedback-preview-table
                    v-if="showPreviewTable"
                    :has-header-row="hasHeaderRow"
                    :table-headers="previewHeaders"
                    :table-data="previewData"
                    :total-rows="previewTotalRows"
                    :filename="csvFilename"
                    :show-has-header-row="showHasHeaderRow"
                    @table-headers-changed="hasHeaderRow = $event" />
            </template>
            <import-feedback-drawer-guide-step
                v-if="showGuideStep"
                :type="chosenSource"
                @poll-count-update="updateImportCounts"
                @guide-selected="guideSelected"
                @poll-selected="pollSelected" />
            <import-feedback-drawer-settings-step
                v-if="isSettingsStep"
                :guide="selectedGuide"
                :poll="selectedPoll"
                :full-import-count="fullImportCount"
                :type="chosenSource"
                @historic-import-change="localHistoricImport = $event"
                @automatic-import-change="localAutomaticImport = $event"
                @update-import-count="updateImportCount"
                @update-timeframe="updateTimeframe"
                @update-ai-flag="updateAnalyzeFlagForPoll" />
            <import-feedback-drawer-import-step
                v-if="isImportingStep"
                :guide="selectedGuide"
                :file-name="csvFilename"
                :type="chosenSource"
                :import-status="importStatus"
                :processed-items="processedItems"
                :total-items="totalItems" />
            <import-feedback-drawer-review-step-wrapper
                v-else-if="isLastStep"
                data-cy="future-response-message"
                :type="chosenSource"
                :completed-import="completedImport"
                :historic-import="localHistoricImport"
                :automatic-import="localAutomaticImport"
                :guide="selectedGuide"
                :review-info-text="reviewInfoText"
                :timeframe="timeframe"
                @view-insights="onViewInsights"
                @view-insights-new-tab="onViewInsightsNewTab" />
        </template>
        <template #footer>
            <div>
                <pendo-button
                    v-if="isMiddleStep && !automaticImportConfig"
                    data-cy="import-feedback-drawer-back-button"
                    theme="app"
                    type="secondary"
                    label="Back"
                    @click="changeStep(-1)" />
                <div class="import-feedback-drawer__next-actions">
                    <pendo-button
                        v-if="isMiddleStep"
                        v-pendo-tooltip="{
                            arrow: true,
                            content: nextButtonTooltip,
                            boundariesElement: 'viewport',
                            trigger: 'hover',
                        }"
                        class="import-feedback-drawer__next-button"
                        data-cy="import-feedback-drawer-next-button"
                        theme="app"
                        type="primary"
                        :label="nextButtonText"
                        :disabled="disableNextButton"
                        :loading="startingImport"
                        @click="handleStepChange" />
                </div>
                <div
                    v-if="isImportingStep || isLastStep"
                    class="import-feedback-drawer__last-step-buttons">
                    <pendo-button
                        v-if="canAddMoreSources"
                        theme="app"
                        type="secondary"
                        label="Add more sources"
                        :disabled="!isLastStep"
                        @click="resetImport(0)" />
                    <pendo-button
                        v-pendo-tooltip="{
                            content: isLastStep && !createdViewId ? 'No view has been created' : null,
                        }"
                        theme="app"
                        type="primary"
                        label="Open feedback view"
                        :disabled="isViewInsightsDisabled"
                        @click="onViewInsights" />
                </div>
            </div>
        </template>
    </pendo-drawer>
</template>

<script>
import { PendoDrawer, PendoButton, PendoModal, PendoTooltip, PendoNotification } from '@pendo/components';
import ImportFeedbackDrawerSourceChooser from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-source-chooser/import-feedback-drawer-source-chooser.vue';
import ImportFeedbackDrawerFileStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-file-step/import-feedback-drawer-file-step.vue';
import ImportFeedbackDrawerMappingStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-mapping-step/import-feedback-drawer-mapping-step.vue';
import ImportFeedbackDrawerFixIssuesStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-fix-issues-step/import-feedback-drawer-fix-issues-step.vue';
import ImportFeedbackDrawerImportStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-import-step/import-feedback-drawer-import-step.vue';
import ImportFeedbackDrawerReviewStepWrapper from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-review-step-wrapper/import-feedback-drawer-review-step-wrapper.vue';
import ImportFeedbackDrawerGuideStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-guide-step/import-feedback-drawer-guide-step.vue';
import ImportFeedbackPreviewTable from '@/voc/components/drawers/import-feedback-drawer/components/import-feedback-preview-table.vue';
import ImportFeedbackDrawerSettingsStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-settings-step/import-feedback-drawer-settings-step.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
    importFeedbackFromCSV,
    importFeedbackFromPollResponses,
    getImportStatus,
    updateAutomaticImportForGuide,
    validateCSV,
} from '@/voc/api/import.api';
import { DEFAULT_FEEDBACK_TAB, MINIMUM_NUMBER_OF_ITEMS_FOR_SUMMARY } from '@/voc/constants/constants';

import isNull from 'lodash/isNull';
import find from 'lodash/find';
import get from 'lodash/get';
import { updateSourceConfig, updateGuideFieldMapping, createSourceConfig } from '@/voc/api/sources.api';
import moment from '@utils/moment-wrapper';
import { hasDiscoverAIAccess } from '@/voc/helpers/permissions.helper.js';
import { getSavedViewForImport } from '@/voc/api/sources.api';
import { canEditGuide } from '@/vuex/modules/guides/guide-permissions.utils';
import { onDrawerOpen } from '@/utils/voc.utils';

const interval = 2000;

function initialState () {
    return {
        hasHeaderRow: true,
        csvData: [],
        csvFilename: '',
        csvTotalRows: 0,
        pendoAssignedFilename: '',
        mappedColumns: {},
        selectedGuide: null,
        importCount: null,
        startingImport: false,
        importJobId: '',
        importStatusInterval: null,
        importStatus: 'not started',
        processedItems: 0,
        totalItems: 0,
        completedImport: null,
        createdViewId: null,
        generateViewSummary: false,
        localAutomaticImport: true,
        fetchingSourceType: false,
        importError: false,
        startingFixIssues: false,
        filteredIssues: [],
        mutations: {},
        isDragging: false,
        skipFixIssuesStep: false,
        isChangingStep: false,
        selectedPoll: null,
        localHistoricImport: true,
        fullImportCount: null,
        timeframe: {
            start: null,
            end: null,
            label: null,
        },
        reviewInfoText: '',
        manualCreatedSavedViewId: null,
        localSavedAiSettings: [],
        context: null,
        createMissingVisitorsAndAccounts: false,
        mismatchVisitorsAndAccountsCount: 0,
        missingVisitorsCount: 0,
        missingAccountsCount: 0,
        missingVisitorAndAccountCount: 0,
    };
}

export default {
    components: {
        PendoDrawer,
        PendoButton,
        PendoModal,
        ImportFeedbackDrawerSourceChooser,
        ImportFeedbackDrawerFileStep,
        ImportFeedbackDrawerMappingStep,
        ImportFeedbackDrawerFixIssuesStep,
        ImportFeedbackDrawerImportStep,
        ImportFeedbackDrawerReviewStepWrapper,
        ImportFeedbackDrawerGuideStep,
        ImportFeedbackPreviewTable,
        ImportFeedbackDrawerSettingsStep,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        automaticImportConfig: {
            type: Object,
            default: null,
        },
        pageOrigin: {
            type: String,
            default: 'listen',
        },
    },
    emits: [
        'closed',
        'startingAutomaticImport',
        'view-created',
        'ai-settings-updated',
        'update:visible',
        'update-source-config-list',
    ],
    data () {
        return {
            currentStepIndex: 0,
            chosenSource: '',
            showExitWarning: false,
            importType: null,
            hasDiscoverAIAccess: hasDiscoverAIAccess(),
            ...initialState(),
            mappingTableData: [
                {
                    id: 1,
                    pendoField: {
                        label: 'Title',
                        value: 'title',
                    },
                    required: true,
                },
                {
                    id: 2,
                    pendoField: {
                        label: 'Description',
                        value: 'description',
                    },
                    required: false,
                },
                {
                    id: 3,
                    pendoField: {
                        label: 'Importance',
                        value: 'importance',
                    },
                    required: false,
                },
                {
                    id: 4,
                    pendoField: {
                        label: 'Account',
                        value: 'account',
                    },
                },
                {
                    id: 5,
                    pendoField: {
                        label: 'Visitor',
                        value: 'visitor',
                    },
                },
                {
                    id: 6,
                    pendoField: {
                        label: 'Product Area',
                        value: 'productArea',
                    },
                    required: false,
                },
                {
                    id: 7,
                    pendoField: {
                        label: 'Labels',
                        value: 'labels',
                    },
                    required: false,
                },
                {
                    id: 8,
                    pendoField: {
                        label: 'Links',
                        value: 'links',
                    },
                    required: false,
                },
                {
                    id: 9,
                    pendoField: {
                        label: 'Submitter',
                        value: 'submitter',
                    },
                    required: false,
                },
                {
                    id: 10,
                    pendoField: {
                        label: 'Submitted Date',
                        value: 'submittedOn',
                    },
                    required: false,
                },
            ],
        };
    },
    computed: {
        ...mapGetters({
            isMultiApp: 'apps/isMultiApp',
        }),
        ...mapState({
            activeGuide: (state) => state.guides.guide,
        }),
        steps () {
            if (this.automaticImportConfig) {
                return ['Settings', 'Apply and review'];
            }
            if (this.chosenSource === 'NPS') {
                return ['Choose NPS', 'Settings', 'Apply and review'];
            }
            if (this.chosenSource === 'guide') {
                return ['Choose guide', 'Settings', 'Apply and review'];
            }
            if (this.chosenSource === 'unknown') {
                return ['Import', 'Review'];
            }

            if (this.hasCSVIssues) {
                return ['Choose file', 'Mapping', 'Fix issues', 'Import', 'Review'];
            }

            return ['Choose file', 'Mapping', 'Import', 'Review'];
        },
        isSourceStep () {
            return this.currentStepIndex === 0;
        },
        isFileStep () {
            return this.currentStepIndex === 1 && this.chosenSource === 'file';
        },
        isMappingStep () {
            return this.currentStepIndex === 2 && this.chosenSource === 'file';
        },
        isFixIssuesStep () {
            if (this.hasCSVIssues) {
                return this.currentStepIndex === 3 && this.chosenSource === 'file';
            }

            return false;
        },
        hasCSVIssues () {
            return (
                (this.filteredIssues.length > 0 ||
                    this.missingAccountsCount > 0 ||
                    this.missingVisitorsCount > 0 ||
                    this.mismatchVisitorsAndAccountsCount > 0 ||
                    this.missingVisitorAndAccountCount > 0) &&
                !this.skipFixIssuesStep
            );
        },
        isMiddleStep () {
            switch (this.chosenSource) {
                case 'file':
                    if (this.hasCSVIssues) {
                        return this.currentStepIndex > 0 && this.currentStepIndex < 4;
                    }

                    return this.currentStepIndex > 0 && this.currentStepIndex < 3;
                case 'guide':
                case 'NPS':
                    return this.currentStepIndex > 0 && this.currentStepIndex < 3;
                default:
                    return false;
            }
        },
        isLastStep () {
            switch (this.chosenSource) {
                case 'file':
                    if (this.hasCSVIssues) {
                        return this.currentStepIndex === 5;
                    }

                    return this.currentStepIndex === 4;
                case 'guide':
                case 'NPS':
                    return this.currentStepIndex === 4;
                case 'unknown':
                    return this.currentStepIndex === 2;
                default:
                    return false;
            }
        },
        isSettingsStep () {
            switch (this.chosenSource) {
                case 'guide':
                case 'NPS':
                    return this.currentStepIndex === 2;
                default:
                    return false;
            }
        },
        isImportingStep () {
            switch (this.chosenSource) {
                case 'file':
                    if (this.hasCSVIssues) {
                        return this.currentStepIndex === 4;
                    }

                    return this.currentStepIndex === 3;

                case 'guide':
                case 'NPS':
                    return this.currentStepIndex === 3;
                case 'unknown':
                    return this.currentStepIndex === 1;
                default:
                    return false;
            }
        },
        isGuideStep () {
            return this.currentStepIndex === 1 && this.chosenSource === 'guide';
        },
        isNPSStep () {
            return this.currentStepIndex === 1 && this.chosenSource === 'NPS';
        },
        disableNextButton () {
            if (this.isChangingStep) {
                return true;
            }

            if (this.isFileStep) {
                return !this.headers.length || !this.previewData.length;
            }

            if (this.isMappingStep) {
                return Object.keys(this.mappedColumns).length !== this.mappingTableData.length;
            }

            if (this.isFixIssuesStep) {
                const productAreaIsRequired = this.filteredIssues.some(
                    (issue) => issue.kind === 'productArea' && !issue.selectedValue,
                );

                if (!this.isMultiApp) {
                    return productAreaIsRequired;
                }

                const appIsRequired = this.filteredIssues.some((issue) => issue.kind === 'app' && !issue.selectedValue);

                return appIsRequired || productAreaIsRequired;
            }
            if (this.isSettingsStep) {
                return !!this.settingsStepDisabledTooltip;
            }
            if (this.isNPSStep || this.isGuideStep) {
                return this.selectedGuide === null;
            }

            return false;
        },
        nextButtonText () {
            if (this.isFixIssuesStep) {
                return 'Import feedback';
            }
            if (this.isNPSStep || this.isGuideStep) {
                return 'Next: Settings';
            }
            if (this.isSettingsStep) {
                return 'Next: Apply and review';
            }

            return 'Next step';
        },
        nextButtonTooltip () {
            if (this.isMappingStep && this.disableNextButton) {
                return 'All columns must be mapped before you can import feedback';
            }
            if (this.isFixIssuesStep && this.disableNextButton) {
                return 'All issues must be fixed before you can import feedback';
            }
            if (this.isSettingsStep) {
                return this.settingsStepDisabledTooltip;
            }

            return null;
        },
        canAddMoreSources () {
            return this.pageOrigin !== 'guides' && this.chosenSource !== 'unknown' && !this.automaticImportConfig;
        },
        headers () {
            if (!this.csvData.length) {
                return [];
            }

            if (this.hasHeaderRow) {
                return this.csvData[0].map((header, index) => ({
                    id: index + 1,
                    label: header.trim().replace(/\*$/, '') || `Column ${index + 1}`,
                    prop: header.trim().replace(/\*$/, '') || `Column ${index + 1}`,
                }));
            }

            return this.csvData[0].map((__, index) => ({
                id: index + 1,
                label: `Column ${index + 1}`,
                prop: `Column ${index + 1}`,
            }));
        },
        mappedHeaders () {
            return Object.keys(this.mappedColumns)
                .filter((key) => {
                    return !isNull(this.mappedColumns[key]);
                })
                .map((key) => {
                    const column = this.mappingTableData.find((row) => row.pendoField.value === key);
                    const sourceField = this.headers[this.mappedColumns[key]]?.label;

                    return {
                        label: column.pendoField.label,
                        prop: sourceField,
                    };
                })
                .sort((a, b) => {
                    const pendoFieldLabels = this.mappingTableData.map((row) => row.pendoField.label);

                    return pendoFieldLabels.indexOf(a.label) - pendoFieldLabels.indexOf(b.label);
                });
        },
        previewHeaders () {
            return this.isFileStep ? this.headers : this.mappedHeaders;
        },
        previewData () {
            if (!this.csvData.length) {
                return [];
            }

            let previewData;
            if (this.hasHeaderRow) {
                previewData = this.csvData.slice(1, 4);
            } else {
                previewData = this.csvData.slice(0, 3);
            }

            return previewData.map((row, rowIndex) => {
                const previewRow = {};
                previewRow.id = rowIndex + 1;
                row.forEach((cell, cellIndex) => {
                    const header = this.headers[cellIndex].prop;
                    const isApp = parseInt(this.columnMappings.columnNumbers.app) === cellIndex;
                    const isProductArea = parseInt(this.columnMappings.columnNumbers.productArea) === cellIndex;
                    if (isApp || isProductArea) {
                        const kind = isApp ? 'app' : 'productArea';
                        const fixedData = find(this.filteredIssues, { kind, value: cell });
                        previewRow[header] =
                            fixedData?.selectedValue?.displayName || fixedData?.selectedValue?.name || cell;

                        return;
                    }
                    previewRow[header] = cell;
                });

                return previewRow;
            });
        },
        previewTotalRows () {
            return this.hasHeaderRow ? this.csvTotalRows - 1 : this.csvTotalRows;
        },
        canEditGuide () {
            return canEditGuide({
                guide: this.selectedGuide,
                action: 'edit',
                source: 'ANY',
                target: 'ANY',
            });
        },
        automaticImportDisabled () {
            return !this.selectedGuide || !this.canEditGuide;
        },
        showPermissionToolTip () {
            return this.selectedGuide && !this.canEditGuide;
        },
        presetImportId () {
            return get(this.$route, 'query.importId');
        },
        isViewInsightsDisabled () {
            return !this.isLastStep || !this.createdViewId;
        },
        columnMappings () {
            const formattedMappedColumns = {};
            Object.keys(this.mappedColumns).forEach((key) => {
                const value = this.mappedColumns[key];
                if (value === null) return;
                formattedMappedColumns[value] = key;
            });

            const columnNumbers = Object.keys(formattedMappedColumns).reduce((acc, key) => {
                acc[formattedMappedColumns[key]] = key;

                return acc;
            }, {});

            return { formattedMappedColumns, columnNumbers };
        },
        showPreviewTable () {
            return (
                (this.isFileStep && (this.headers.length > 0 || this.previewData.length > 0)) ||
                (this.isMappingStep && this.mappedHeaders.length > 0 && this.previewData.length > 0) ||
                (this.isFixIssuesStep && this.hasCSVIssues)
            );
        },
        showHasHeaderRow () {
            return this.isFileStep;
        },
        guideFieldMappings () {
            return this.selectedGuide.polls.map((poll, index) => ({
                fieldName: 'FormData',
                fieldPosition: index + 1,
                subObjectId: poll.id,
                subObjectName: 'Poll',
            }));
        },
        npsFieldMappings () {
            const priorityMap = {
                NPSReason: 1,
                NPSRating: 2,
                default: 3,
            };

            const reorderedPolls = [...this.selectedGuide.polls].sort((a, b) => {
                const priorityA = priorityMap[a.attributes.type] || priorityMap.default;
                const priorityB = priorityMap[b.attributes.type] || priorityMap.default;

                return priorityA - priorityB;
            });

            const fieldMapping = reorderedPolls.map((poll, index) => ({
                fieldName: 'FormData',
                fieldPosition: index + 1,
                subObjectId: poll.id,
                subObjectName: 'Poll',
            }));

            return fieldMapping;
        },

        newAutoCreateValue () {
            return this.selectedGuide?.autoCreateFeedback !== this.localAutomaticImport;
        },
        settingsStepDisabledTooltip () {
            if (!this.isSettingsStep) return null;
            if (!this.aiAnalyzeValidated) {
                return 'Please select an AI analysis option for each poll';
            }
            if (
                (!this.localHistoricImport || this.importCount === 0) &&
                !this.newAutoCreateValue &&
                !this.aiAnalyzeChanged
            ) {
                return 'No changes to apply. Update settings to proceed.';
            }

            return null;
        },
        isBreadcrumbActive () {
            return (index) => {
                let offsetIndex = index;
                if (this.isGuideOrNPS && this.automaticImportConfig) {
                    offsetIndex += 1;
                }
                if (this.isGuideOrNPS && this.isImportingStep) {
                    return false;
                }
                if (this.isGuideOrNPS && this.isLastStep) {
                    return offsetIndex === 2;
                }

                return offsetIndex === this.currentStepIndex - 1;
            };
        },
        showGuidePollTitle () {
            return this.chosenSource === 'guide' && this.currentStepIndex !== 0;
        },
        showGuideStep () {
            return this.isGuideStep || this.isNPSStep;
        },
        aiAnalyzeValidated () {
            if (!this.aiSettingsEnabled) return true;

            return this.selectedGuide?.polls?.every((poll) => !!poll.analyze);
        },
        aiAnalyzeChanged () {
            if (!this.aiSettingsEnabled) return false;
            const currentSavedAiSettings = this.localSavedAiSettings;
            const updatedAiSettings = this.selectedGuide?.polls?.map((poll) => poll.analyze) || [];

            return JSON.stringify(currentSavedAiSettings) !== JSON.stringify(updatedAiSettings);
        },
        aiSettingsEnabled () {
            return this.isGuideOrNPS && this.hasDiscoverAIAccess;
        },
        isGuideOrNPS () {
            return this.chosenSource === 'guide' || this.chosenSource === 'NPS';
        },
        allowCreateMissingVisitorsAndAccounts () {
            return this.chosenSource === 'file';
        },
    },
    watch: {
        currentStepIndex (step) {
            if (step === 0) {
                this.chosenSource = '';
                this.resetImport(0);
            }
        },
        hasHeaderRow (hasHeaderRow) {
            if (this.csvData.length === 0) return;
            this.mappedColumns = {};
            if (hasHeaderRow) this.mapSimilarColumns();
        },
        automaticImportConfig (config) {
            if (!config) return;
            this.chosenSource = config.chosenSource;
            this.currentStepIndex = config.currentStepIndex;
            this.localSavedAiSettings = config.guide?.polls?.map((poll) => poll.analyze);
            this.selectedGuide = config.guide;
            this.selectedPoll = config.selectedPoll;
            this.$emit('startingAutomaticImport');
        },
        context: {
            async handler (context) {
                if (context === 'fileSource') {
                    this.chooseSource('file');
                } else if (context === 'guideSource') {
                    this.chooseSource('guide');
                } else if (context === 'NPS') {
                    this.chooseSource('NPS');
                } else if (context === 'import') {
                    this.fetchingSourceType = true;
                    if (this.presetImportId) {
                        this.importJobId = this.presetImportId;
                        this.goToImportStep();
                        await this.getImportStatus();
                        if (!this.importError && this.importStatus !== 'complete') {
                            this.fetchingSourceType = false;
                            this.startCheckingImportStatus();
                        }
                    } else {
                        this.confirmImportError();
                    }
                }
            },
            immediate: true,
        },
        visible (newVisible) {
            if (newVisible) {
                this.loadStatuses({ type: 'feedback' });
                onDrawerOpen();
                this.setContext();
            }
        },
    },
    created () {
        if (this.visible) {
            this.loadStatuses({ type: 'feedback' });
        }

        if (!this.isMultiApp) {
            this.onMapColumn('app', null);
        } else {
            this.mappingTableData.splice(5, 0, {
                id: 11,
                pendoField: {
                    label: 'App',
                    value: 'app',
                },
                required: true,
            });
        }
    },
    mounted () {
        window.addEventListener('popstate', this.handlePopState);
    },
    beforeUnmount () {
        window.removeEventListener('popstate', this.handlePopState);
    },
    methods: {
        ...mapActions({
            loadStatuses: 'statuses/loadAll',
            loadSavedView: 'savedViews/loadOne',
            updateCurrentSavedView: 'savedViews/updateCurrentSavedView',
            loadImportTips: 'voc/onboarding/loadImportTips',
        }),
        ...mapMutations({
            setActiveGuide: 'guides/setActiveGuide',
        }),
        exitImport () {
            if (this.isSourceStep || this.isLastStep) {
                this.confirmExit();
            } else {
                this.showExitWarning = true;
            }
        },
        confirmExit () {
            document
                .querySelector('.import-feedback-drawer .pendo-drawer__body')
                .removeEventListener('dragenter', this.onDragEnter);
            this.isDragging = false;
            this.showExitWarning = false;
            this.currentStepIndex = 0;
            clearInterval(this.importStatusInterval);
            this.$emit('closed');
        },
        changeStep (stepIndex) {
            this.currentStepIndex += stepIndex;
            this.isChangingStep = false;
        },
        chooseSource (source) {
            this.chosenSource = source;
            this.changeStep(1);
        },
        fileUploaded ({ data, totalRows, userAssignedFileName, pendoAssignedFileName }) {
            this.csvData = data || [];
            this.csvTotalRows = totalRows || 0;
            this.mappedColumns = {};
            this.csvFilename = userAssignedFileName;
            this.pendoAssignedFilename = pendoAssignedFileName;
            if (this.csvData.length !== 0) {
                this.mapSimilarColumns();
            }
        },
        resetImport (index) {
            this.currentStepIndex = index;
            for (const [key, value] of Object.entries(initialState())) {
                this[key] = value;
            }
        },
        onMapColumn (pendoField, sourceField) {
            if (sourceField === undefined) {
                delete this.mappedColumns[pendoField];

                return;
            }
            this.mappedColumns[pendoField] = sourceField;
        },
        onCreateMissingVisitorsAndAccountsChange (value) {
            this.createMissingVisitorsAndAccounts = value;
        },
        checkForMissingVisitorsAndAccounts (response) {
            this.missingVisitorAndAccountCount = response.missingVisitorAndAccountCount || 0;

            this.missingVisitorsCount = 0;
            this.missingAccountsCount = 0;
            this.mismatchVisitorsAndAccountsCount = 0;
            if (!this.allowCreateMissingVisitorsAndAccounts) {
                return;
            }
            this.missingVisitorsCount = response.missingVisitorsCount || 0;
            this.missingAccountsCount = response.missingAccountsCount || 0;
            this.mismatchVisitorsAndAccountsCount = response.mismatchVisitorsAndAccountsCount || 0;
        },
        onOpen () {
            this.loadImportTips();
            this.$nextTick(() => {
                document
                    .querySelector('.import-feedback-drawer .pendo-drawer__body')
                    .addEventListener('dragenter', this.onDragEnter);
            });
        },
        onDragEnter () {
            this.isDragging = true;
        },
        onDragLeave () {
            this.isDragging = false;
        },
        mapSimilarColumns () {
            const headers = this.headers.map((header) => header.label);
            const pendoFields = this.mappingTableData.map((row) => row.pendoField.value);

            pendoFields.forEach((pendoField) => {
                let index = headers.findIndex(
                    (header) => header.toLowerCase().replace(' ', '') === pendoField.toLowerCase(),
                );

                if (index === -1) {
                    const fieldData = this.mappingTableData.find((row) => row.pendoField.value === pendoField);
                    const { label } = fieldData.pendoField;

                    if (label.toLowerCase().replace(' ', '') !== pendoField.toLowerCase()) {
                        index = headers.findIndex(
                            (header) => header.toLowerCase().replace(' ', '') === label.toLowerCase().replace(' ', ''),
                        );
                    }
                }

                if (index !== -1) {
                    this.mappedColumns[pendoField] = index;
                }
            });
        },
        guideSelected (guide) {
            this.localAutomaticImport = true;
            if (guide) {
                this.localSavedAiSettings = guide?.polls?.map((poll) => poll.analyze);
            }
            this.selectedGuide = guide;
        },
        pollSelected (poll) {
            this.selectedPoll = poll;
        },
        updateImportCount (count) {
            this.importCount = count;
        },
        updateImportCounts (count) {
            this.fullImportCount = count;
            this.importCount = count;
        },
        handleStepChange () {
            this.isChangingStep = true;
            switch (this.chosenSource) {
                case 'file':
                    if (this.isFixIssuesStep) {
                        this.generateMutations();
                        this.importCSV();
                        this.changeStep(1);
                    }
                    if (this.isMappingStep) {
                        this.validateCSV();
                    }
                    if (this.isFileStep) {
                        this.changeStep(1);
                    }
                    break;

                case 'guide':
                case 'NPS':
                    if (!this.isSettingsStep) {
                        this.changeStep(1);

                        return;
                    }
                    this.importPollResponses();
            }
        },
        onFixIssueUpdate ({ row, value }) {
            const issueIndex = this.filteredIssues.findIndex(
                (issue) => issue.kind === row.kind && issue.value === row.value,
            );
            this.filteredIssues[issueIndex].selectedValue = value;
        },
        filterAppsAndProductAreasIssues (issues) {
            this.filteredIssues = issues
                .filter((issue) => issue.kind === 'app' || issue.kind === 'productArea')
                .sort((a, b) => {
                    if (a.kind === 'app' && b.kind === 'productArea') return -1;
                    if (a.kind === 'productArea' && b.kind === 'app') return 1;

                    return a.value.localeCompare(b.value);
                });
        },
        generateMutations () {
            const { columnNumbers } = this.columnMappings;

            this.filteredIssues.forEach((issue) => {
                const columnNumber = columnNumbers[issue.kind];
                if (!this.mutations[columnNumber]) this.mutations[columnNumber] = {};

                let correctedValue;
                if (issue.kind === 'app') {
                    correctedValue = issue.selectedValue?.displayName;
                } else if (issue.kind === 'productArea' && issue.selectedValue?.name === 'No Product Area') {
                    correctedValue = '';
                } else {
                    correctedValue = issue.selectedValue?.name;
                }

                this.mutations[columnNumber][issue.value] = correctedValue;
            });
        },
        async validateCSV () {
            this.startingFixIssues = true;
            this.generateMutations();

            try {
                const response = await validateCSV({
                    importId: this.pendoAssignedFilename,
                    mappings: this.columnMappings.formattedMappedColumns,
                    includesHeadersRow: this.hasHeaderRow,
                    mutations: this.mutations,
                    createMissingVisitorsAndAccounts: this.createMissingVisitorsAndAccounts,
                });
                this.checkForMissingVisitorsAndAccounts(response);
                this.filterAppsAndProductAreasIssues(response.errors);
                this.$nextTick(() => {
                    if (
                        Object.keys(this.mutations).length === 0 &&
                        this.filteredIssues.length === 0 &&
                        this.missingAccountsCount === 0 &&
                        this.missingVisitorsCount === 0 &&
                        this.mismatchVisitorsAndAccountsCount === 0 &&
                        this.missingVisitorAndAccountCount === 0
                    ) {
                        this.skipFixIssuesStep = true;
                        this.importCSV();
                    } else {
                        this.skipFixIssuesStep = false;
                    }
                });
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'Fix issues step could not load',
                    message: 'There was an error whilst starting the import, please try again',
                    duration: 3000,
                });
            } finally {
                this.startingFixIssues = false;
                this.changeStep(1);
            }
        },
        async importCSV () {
            this.startingImport = true;
            try {
                const importId = await importFeedbackFromCSV({
                    importId: this.pendoAssignedFilename,
                    mappings: this.columnMappings.formattedMappedColumns,
                    includesHeadersRow: this.hasHeaderRow,
                    filename: this.csvFilename,
                    mutations: this.mutations,
                    createMissingVisitorsAndAccounts: this.createMissingVisitorsAndAccounts,
                });
                this.importJobId = importId;
                this.$emit('update-source-config-list', {
                    fetch: true,
                    sourceConfig: { domain: 'Internal', objectName: 'CSV', objectId: this.csvFilename },
                });
                this.startCheckingImportStatus();
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'Import not started',
                    message: 'There was an error whilst starting the import, please try again',
                    duration: 3000,
                });
            } finally {
                this.startingImport = false;
            }
        },
        async importPollResponses () {
            this.startingImport = true;
            try {
                const newSourceConfig = await this.updateMappings();
                if (this.newAutoCreateValue) {
                    await updateAutomaticImportForGuide({
                        guideId: this.selectedGuide.id,
                        autoCreateFeedback: this.localAutomaticImport,
                    });
                    if (this.activeGuide?.id === this.selectedGuide.id) {
                        const updatedActiveGuide = {
                            ...this.activeGuide,
                            autoCreateFeedback: this.localAutomaticImport,
                        };
                        this.setActiveGuide({ guide: updatedActiveGuide });
                    }
                    this.reviewInfoText = this.localAutomaticImport
                        ? 'Future responses will be brought into Listen'
                        : 'Future responses will not be brought into Listen';
                }
                if (this.localHistoricImport && this.importCount !== 0) {
                    const importId = await importFeedbackFromPollResponses({
                        guideId: this.selectedGuide.id,
                        pollId: this.selectedPoll?.id,
                        timeframe: this.timeframe,
                    });
                    this.importJobId = importId;
                    this.startCheckingImportStatus();
                    this.changeStep(1);
                    this.$emit('update-source-config-list', { fetch: true, sourceConfig: newSourceConfig });
                } else {
                    await this.savedViewCreationCheck();
                    this.completedImport = {
                        ...(this.createdViewId && { savedViewId: this.createdViewId }),
                    };
                    this.changeStep(2);
                }
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'Import not started',
                    message: 'There was an error whilst starting the import, please try again',
                    duration: 3000,
                });
            } finally {
                this.startingImport = false;
            }
        },
        startCheckingImportStatus () {
            this.getImportStatus();
            clearInterval(this.importStatusInterval);
            this.importStatusInterval = setInterval(() => {
                this.getImportStatus();
            }, interval);
        },
        async getImportStatus () {
            try {
                this.importError = false;
                this.generateViewSummary = false;

                const data = await getImportStatus({ jobId: this.importJobId });

                this.importStatus = data.status;
                this.importType = data.source?.source;

                const totalErrors = data.errors?.length || 0;
                this.totalItems = data.totalItemCount;
                this.processedItems = data.countSavedToDatastore + totalErrors;

                if (data.savedViewId?.length > 0 && !this.createdViewId) {
                    this.$emit('view-created');
                    this.generateViewSummary = data.countSavedToDatastore >= MINIMUM_NUMBER_OF_ITEMS_FOR_SUMMARY;
                    this.createdViewId = data.savedViewId;
                    await this.loadSavedView({ id: data.savedViewId })
                        .then(() => {
                            this.updateCurrentSavedView({
                                property: 'shouldGenerateSummary',
                                value: this.generateViewSummary,
                                persist: false,
                            });
                        })
                        .catch(() => {});
                }

                if (this.importStatus === 'failed') {
                    PendoNotification({
                        type: 'error',
                        title: 'Something went wrong.',
                        message: 'An unexpected error occurred during import. Please try again',
                        duration: 3000,
                    });
                    console.error(data.failureMessage);
                    this.importError = true;
                    this.confirmExit();
                } else if (this.importStatus === 'complete') {
                    setTimeout(() => {
                        clearInterval(this.importStatusInterval);
                        this.completedImport = data;
                        this.changeStep(1);
                        this.fetchingSourceType = false;
                        // If import is complete, re-trigger the voc item count
                    }, 500);
                }
            } catch {
                this.confirmImportError();
            }
        },
        onViewInsights () {
            window.location.href = this.$router.pendo.toPath({
                name: 'ValidateFeedbackSavedViewTabs',
                params: {
                    savedviewId: this.completedImport.savedViewId,
                    tab: DEFAULT_FEEDBACK_TAB,
                },
            });
        },
        onViewInsightsNewTab () {
            window.open(
                this.$router.pendo.toPath({
                    name: 'ValidateFeedbackSavedViewTabs',
                    params: {
                        savedviewId: this.completedImport.savedViewId,
                        tab: DEFAULT_FEEDBACK_TAB,
                    },
                }),
                '_blank',
            );
        },
        updateAutomaticImport (value) {
            this.localAutomaticImport = value;
        },
        goToImportStep () {
            switch (this.importType) {
                case 'CSV':
                    this.chosenSource = 'file';
                    this.currentStepIndex = 3;
                    break;
                case 'PollResponse':
                    this.chosenSource = 'guide';
                    this.currentStepIndex = 2;
                    break;
                default:
                    this.chosenSource = 'unknown';
                    this.currentStepIndex = 1;
                    break;
            }
        },
        confirmImportError () {
            PendoNotification({
                type: 'error',
                title: 'Something went wrong.',
                message: 'Either the import has expired/does not exist, or we were not able to fetch the import.',
                duration: 3000,
            });
            this.importError = true;
            this.confirmExit();
        },
        async updateMappings () {
            let vocFieldMappingPayload;
            switch (this.chosenSource) {
                case 'guide':
                    vocFieldMappingPayload = this.guideFieldMappings;
                    break;
                case 'NPS':
                    vocFieldMappingPayload = this.npsFieldMappings;
                    break;
                default:
                    return;
            }
            const analyzeAIPollIds = this.selectedGuide.polls
                .filter((poll) => poll.analyze === 'analyze')
                .map((poll) => poll.id);
            let sourceConfig;
            if (this.selectedGuide?.sourceConfigId) {
                const startTime = this.newAutoCreateValue ? moment().valueOf() : this.selectedGuide.autoCreateStartTime;
                [sourceConfig] = await Promise.all([
                    updateSourceConfig('Internal', 'Guide', this.selectedGuide.id, {
                        subObjectIdsToSendToAI: analyzeAIPollIds,
                        sendToAIConfirmed: true,
                        startTime,
                        isEnabled: this.localAutomaticImport,
                        isContinuousIntegration: this.localAutomaticImport,
                    }),
                    updateGuideFieldMapping({
                        guideId: this.selectedGuide.id,
                        vocFieldMappingPayload,
                    }),
                ]);
            } else {
                [sourceConfig] = await Promise.all([
                    createSourceConfig('Internal', 'Guide', this.selectedGuide.id, {
                        subObjectIdsToSendToAI: analyzeAIPollIds,
                        sendToAIConfirmed: true,
                        startTime: moment().valueOf(),
                        isEnabled: this.localAutomaticImport,
                        isContinuousIntegration: this.localAutomaticImport,
                    }),
                    updateGuideFieldMapping({
                        guideId: this.selectedGuide.id,
                        vocFieldMappingPayload,
                    }),
                ]);
            }
            this.$emit('update-source-config-list', { fetch: false, sourceConfig });
            if (this.aiSettingsEnabled) {
                this.$emit('ai-settings-updated', analyzeAIPollIds);
            }

            return sourceConfig;
        },
        updateTimeframe (config) {
            let startTime;
            let endTime;
            if (config.type.includes('Last')) {
                startTime = moment(config.start, 'YYYY-MM-DD')
                    .add(1, 'days')
                    .valueOf();
            } else if (config.type.includes('Custom')) {
                startTime = moment(config.start, 'YYYY-MM-DD')
                    .add(1, 'days')
                    .valueOf();
                endTime = moment(config.end, 'YYYY-MM-DD').valueOf();
            }

            const now = moment().valueOf();
            if (endTime > now) {
                endTime = now;
            }

            this.timeframe = { start: startTime, end: endTime, label: config.type };
        },
        async savedViewCreationCheck () {
            if (this.localAutomaticImport) {
                const savedViewId = await getSavedViewForImport({
                    importType: 'guide',
                    importId: this.selectedGuide.id,
                });
                this.createdViewId = savedViewId;
                this.loadSavedView({ id: savedViewId });
            }
        },
        updateAnalyzeFlagForPoll (pollId, value) {
            this.selectedGuide.polls.find((poll) => poll.id === pollId).analyze = value;
        },
        handlePopState () {
            if (this.visible) {
                this.$emit('update:visible', false);
                this.$emit('closed');
            }
        },
        setContext () {
            const { context } = this.$route?.query;
            if (context) {
                this.context = context;
                this.$router?.replace({ context: null });
            }
        },
    },
};
</script>

<style scoped lang="less">
.import-feedback-drawer {
    &__title {
        font-size: 18px;
        font-weight: 600;
    }

    &__breadcrumbs {
        &-name {
            font-size: 16px;
            color: var(--palette-gray-60);

            &--active {
                color: var(--palette-gray-100);
            }
        }

        &-separator {
            font-size: 18px;
            margin: auto 15px;
            color: var(--palette-gray-60);
        }
    }

    &__exit {
        min-width: 88px;
    }

    &__exit-modal {
        ul {
            margin: 10px 0;
        }
    }

    &__next-actions {
        float: right;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 16px;
    }

    &__last-step-buttons {
        float: right;
    }

    &__footer-buttons {
        display: flex;
        justify-content: flex-end;
    }

    &__chosen-container {
        height: 95%;
        width: 100%;
    }

    &__automatic-import {
        display: flex;
        flex-direction: row;
        gap: 8px;
    }

    &__automatic-import-text {
        padding: 0;
        margin: 0;
    }

    &__info-icon {
        display: flex;
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.import-feedback-drawer {
    .pendo-drawer__body {
        padding: 64px 0 !important;
    }
    &--hide-footer {
        .pendo-drawer__footer {
            display: none;
        }
        .pendo-drawer__content {
            grid-template-rows: 68px auto 0 !important;
        }
    }
    @media (max-width: 859px) {
        .pendo-drawer__content {
            grid-template-rows: minmax(0, max-content) auto minmax(68px, max-content) !important;
        }
        &--hide-footer {
            .pendo-drawer__content {
                grid-template-rows: minmax(0, max-content) auto 0 !important;
            }
        }
        .pendo-drawer__header {
            padding: 10px 0;
            flex-direction: column;
            flex-wrap: nowrap;
            justify-content: center;
        }

        .import-feedback-drawer__breadcrumbs {
            margin-top: 4px;

            & + .import-feedback-drawer__exit .pendo-button {
                margin-top: 8px;
            }
        }

        .import-feedback-drawer__exit .pendo-button {
            margin-top: 4px;
        }
    }
}
</style>
